import React, { useEffect, useState, useRef, createElement } from 'react';
import { CameraIcon, CheckCircleIcon, XIcon, PlusIcon, Loader2Icon, AlertCircleIcon, MailIcon, EyeIcon, EyeOffIcon, MoreVerticalIcon, ImageIcon, Trash2Icon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/firebaseConfig';
import { updateProfile, verifyBeforeUpdateEmail, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth as firebaseAuth } from '../../firebase/firebaseConfig';
import { uploadFile, generateStoragePath } from '../../firebase/storage';
export const ProfileSection = () => {
  const {
    user,
    auth
  } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bannerFileInputRef = useRef<HTMLInputElement>(null);
  // State management
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isVerified: false,
    profileImage: '',
    profileBanner: '',
    publicUsername: '',
    languagesSpoken: [] as string[]
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    ...userData
  });
  const [customLanguage, setCustomLanguage] = useState('');
  const [showCustomLanguageInput, setShowCustomLanguageInput] = useState(false);
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Image upload states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [showBannerMenu, setShowBannerMenu] = useState(false);
  const bannerMenuRef = useRef<HTMLDivElement>(null);
  // Email change states
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState<string | null>(null);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  // Available languages
  const availableLanguages = ['English', 'Spanish', 'French', 'German', 'Arabic', 'Chinese', 'Hindi', 'Russian', 'Armenian', 'Japanese', 'Korean', 'Portuguese'];
  // Fetch user data from Firestore
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const userDocRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          // Split name into first and last name if needed
          let firstName = data.firstName || '';
          let lastName = data.lastName || '';
          if (!firstName && !lastName && data.name) {
            const nameParts = data.name.split(' ');
            firstName = nameParts[0] || '';
            lastName = nameParts.slice(1).join(' ') || '';
          }
          const profileData = {
            firstName,
            lastName,
            email: user.email || data.email || '',
            phone: data.phone || '',
            isVerified: !!data.emailVerified,
            profileImage: data.profileImage || user.photoURL || '',
            profileBanner: data.profileBanner || '',
            publicUsername: data.publicUsername || '',
            languagesSpoken: data.languagesSpoken || []
          };
          setUserData(profileData);
          setFormData(profileData);
        } else {
          // Create a new user document if it doesn't exist
          const newUserData = {
            firstName: '',
            lastName: '',
            email: user.email || '',
            name: user.name || '',
            phone: '',
            isVerified: !!user.emailVerified,
            profileImage: user.photoURL || '',
            profileBanner: '',
            publicUsername: '',
            languagesSpoken: [],
            createdAt: new Date()
          };
          await setDoc(userDocRef, newUserData);
          // Split name into first and last name
          let firstName = '';
          let lastName = '';
          if (user.name) {
            const nameParts = user.name.split(' ');
            firstName = nameParts[0] || '';
            lastName = nameParts.slice(1).join(' ') || '';
          }
          const profileData = {
            firstName,
            lastName,
            email: user.email || '',
            phone: '',
            isVerified: !!user.emailVerified,
            profileImage: user.photoURL || '',
            profileBanner: '',
            publicUsername: '',
            languagesSpoken: []
          };
          setUserData(profileData);
          setFormData(profileData);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load profile data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, [user]);
  // Close banner menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (bannerMenuRef.current && !bannerMenuRef.current.contains(e.target as Node)) {
        setShowBannerMenu(false);
      }
    };
    if (showBannerMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBannerMenu]);
  const handleRemoveBanner = async () => {
    setShowBannerMenu(false);
    setBannerPreview(null);
    setFormData((prev: any) => ({
      ...prev,
      profileBanner: ''
    }));
    if (user?.id) {
      try {
        const userDocRef = doc(db, 'users', user.id);
        await updateDoc(userDocRef, {
          profileBanner: ''
        });
        setUserData((prev: any) => ({
          ...prev,
          profileBanner: ''
        }));
        setSuccessMessage('Banner removed successfully.');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        console.error('Error removing banner:', err);
        setError('Failed to remove banner. Please try again.');
      }
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    // Handle special validation for username (no spaces)
    if (name === 'publicUsername') {
      const noSpacesValue = value.replace(/\s+/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: noSpacesValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const handleLanguageToggle = (language: string) => {
    setFormData(prev => {
      const currentLanguages = [...prev.languagesSpoken];
      if (currentLanguages.includes(language)) {
        return {
          ...prev,
          languagesSpoken: currentLanguages.filter(lang => lang !== language)
        };
      } else {
        return {
          ...prev,
          languagesSpoken: [...currentLanguages, language]
        };
      }
    });
  };
  const handleAddCustomLanguage = () => {
    if (customLanguage.trim() && !formData.languagesSpoken.includes(customLanguage.trim())) {
      setFormData(prev => ({
        ...prev,
        languagesSpoken: [...prev.languagesSpoken, customLanguage.trim()]
      }));
      setCustomLanguage('');
      setShowCustomLanguageInput(false);
    }
  };
  const handleRemoveLanguage = (language: string) => {
    setFormData(prev => ({
      ...prev,
      languagesSpoken: prev.languagesSpoken.filter(lang => lang !== language)
    }));
  };
  // Handle profile image click to trigger file input
  const handleProfileImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Handle banner image click to trigger file input
  const handleBannerImageClick = () => {
    if (bannerFileInputRef.current) {
      bannerFileInputRef.current.click();
    }
  };
  // Handle file selection for profile image
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
      return;
    }
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }
    // Create a preview
    const reader = new FileReader();
    reader.onload = event => {
      setImagePreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);
    // Upload to Firebase Storage
    uploadImageToFirebase(file, 'profile');
  };
  // Handle file selection for banner image
  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
      return;
    }
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }
    // Create a preview
    const reader = new FileReader();
    reader.onload = event => {
      setBannerPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);
    // Upload to Firebase Storage
    uploadImageToFirebase(file, 'banner');
  };
  // Upload image to Firebase Storage
  const uploadImageToFirebase = async (file: File, type: 'profile' | 'banner') => {
    if (!user?.id) return;
    const isProfile = type === 'profile';
    if (isProfile) {
      setIsUploading(true);
      setUploadProgress(0);
    } else {
      setIsBannerUploading(true);
      setBannerUploadProgress(0);
    }
    setError(null);
    try {
      // Process image before uploading (resize and optimize)
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      // Create a promise to handle the image processing
      const processedFile = await new Promise<File>(resolve => {
        img.onload = () => {
          // Calculate new dimensions (max 512x512 for profile photos, max 1200x300 for banners)
          let maxWidth = isProfile ? 512 : 1200;
          let maxHeight = isProfile ? 512 : 300;
          let width = img.width;
          let height = img.height;
          if (width > maxWidth || height > maxHeight) {
            if (isProfile) {
              // For profile, maintain aspect ratio but fit within square
              if (width > height) {
                height = height * maxWidth / width;
                width = maxWidth;
              } else {
                width = width * maxHeight / height;
                height = maxHeight;
              }
            } else {
              // For banner, fit to banner dimensions
              width = maxWidth;
              height = maxHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);
          // Convert to blob
          canvas.toBlob(blob => {
            if (blob) {
              // Create new file from blob
              const optimizedFile = new File([blob], file.name, {
                type: file.type
              });
              resolve(optimizedFile);
            } else {
              resolve(file); // Fallback to original if conversion fails
            }
          }, file.type, 0.8);
        };
        img.onerror = () => {
          resolve(file); // Fallback to original if loading fails
        };
        // Load image
        const reader = new FileReader();
        reader.onload = e => {
          if (e.target?.result) {
            img.src = e.target.result as string;
          }
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      });
      // Generate path using the same function used by other components
      // Use 'thumbnail' for both profile and banner to ensure compatibility with storage rules
      const imageType = isProfile ? 'thumbnail' : 'thumbnail';
      const path = generateStoragePath(user.id, imageType, processedFile.name);
      // Use the uploadFile helper that's used by other components
      const {
        uploadTask
      } = uploadFile(processedFile, path, progress => {
        if (isProfile) {
          setUploadProgress(progress);
        } else {
          setBannerUploadProgress(progress);
        }
      }, error => {
        console.error(`Error uploading ${type} image:`, error);
        // Clear success before showing error — never show both
        setSuccessMessage(null);
        setError(`Failed to upload ${type} image. Please try again.`);
        if (isProfile) {
          setIsUploading(false);
        } else {
          setIsBannerUploading(false);
        }
      }, async downloadURL => {
        // Clear error before proceeding — upload succeeded
        setError(null);
        try {
          if (isProfile) {
            setFormData(prev => ({
              ...prev,
              profileImage: downloadURL
            }));
            setIsUploading(false);
            // Auto-save the profile image without requiring form submission
            await saveImageToFirestore(downloadURL, 'profileImage');
          } else {
            setFormData(prev => ({
              ...prev,
              profileBanner: downloadURL
            }));
            setIsBannerUploading(false);
            // Auto-save the banner image without requiring form submission
            await saveImageToFirestore(downloadURL, 'profileBanner');
          }
        } catch (callbackErr) {
          // Catch any unexpected errors — saveImageToFirestore handles its own errors,
          // but this is a safety net to prevent errors from propagating to uploadFile
          console.warn('Unexpected error in upload completion callback:', callbackErr);
        }
      });
    } catch (err) {
      console.error('Error processing or starting upload:', err);
      setSuccessMessage(null);
      setError(`Failed to upload ${type} image. Please try again.`);
      if (isProfile) {
        setIsUploading(false);
      } else {
        setIsBannerUploading(false);
      }
    }
  };
  // Save image to Firestore without requiring full form submission
  const saveImageToFirestore = async (imageUrl: string, field: 'profileImage' | 'profileBanner') => {
    if (!user?.id) return;
    try {
      const userDocRef = doc(db, 'users', user.id);
      // Update just the specific field in Firestore (critical operation)
      await updateDoc(userDocRef, {
        [field]: imageUrl,
        updatedAt: new Date()
      });
      // Update local state
      setUserData(prev => ({
        ...prev,
        [field]: imageUrl
      }));
      // Clear any previous error before showing success
      setError(null);
      setSuccessMessage(`Profile ${field === 'profileImage' ? 'image' : 'banner'} updated successfully!`);
      // Clear message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      // Clear any success message before showing error
      setSuccessMessage(null);
      setError(`Failed to update ${field}. Please try again.`);
      return; // Exit early — don't attempt auth profile update
    }
    // Fire-and-forget: Update auth profile OUTSIDE try/catch
    // This can never interfere with success/error state above
    if (field === 'profileImage' && auth.currentUser) {
      updateProfile(auth.currentUser, {
        photoURL: imageUrl
      }).catch(authErr => {
        console.warn('Auth profile photo update failed (non-critical):', authErr);
      });
    }
  };
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const userDocRef = doc(db, 'users', user.id);
      // Prepare data for Firestore
      const updatedData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
        profileImage: formData.profileImage,
        profileBanner: formData.profileBanner,
        publicUsername: formData.publicUsername,
        languagesSpoken: formData.languagesSpoken,
        updatedAt: new Date()
      };
      // Update Firestore document (critical operation)
      await updateDoc(userDocRef, updatedData);
      // Update local state with the successful changes
      setUserData({
        ...formData
      });
      setSuccessMessage('Profile updated successfully!');
      setIsEditing(false);
      // Clear any image preview since it's now saved
      setImagePreview(null);
      setBannerPreview(null);
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setSuccessMessage(null);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
    // Fire-and-forget: Update auth profile OUTSIDE try/catch
    // This can never interfere with success/error state above
    if (auth.currentUser) {
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();
      updateProfile(auth.currentUser, {
        displayName,
        photoURL: formData.profileImage
      }).catch(authErr => {
        console.warn('Auth profile update failed (non-critical):', authErr);
      });
    }
  };
  // Cancel editing and reset form
  const handleCancelEdit = () => {
    setFormData({
      ...userData
    });
    setImagePreview(null);
    setBannerPreview(null);
    setError(null);
    setSuccessMessage(null);
    setIsEditing(false);
  };
  // Handle email change with re-authentication
  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || !emailChangePassword) {
      setEmailChangeError('Please fill in all fields.');
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailChangeError('Please enter a valid email address.');
      return;
    }
    if (newEmail === userData.email) {
      setEmailChangeError('New email must be different from your current email.');
      return;
    }
    setIsChangingEmail(true);
    setEmailChangeError(null);
    setEmailChangeSuccess(null);
    try {
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser || !currentUser.email) {
        setEmailChangeError('No authenticated user found. Please log in again.');
        setIsChangingEmail(false);
        return;
      }
      // Step 1: Re-authenticate with current password
      const credential = EmailAuthProvider.credential(currentUser.email, emailChangePassword);
      await reauthenticateWithCredential(currentUser, credential);
      // Step 2: Send verification to new email before updating
      await verifyBeforeUpdateEmail(currentUser, newEmail);
      // Step 3: Update Firestore user document
      if (user?.id) {
        const userDocRef = doc(db, 'users', user.id);
        await updateDoc(userDocRef, {
          email: newEmail,
          updatedAt: new Date()
        });
      }
      // Success
      setEmailChangeSuccess('A verification email has been sent to your new address. Please verify it to complete the change.');
      setNewEmail('');
      setEmailChangePassword('');
      // Update local state
      setUserData(prev => ({
        ...prev,
        email: newEmail
      }));
      setFormData(prev => ({
        ...prev,
        email: newEmail
      }));
      // Auto-close after 5 seconds
      setTimeout(() => {
        setShowEmailChange(false);
        setEmailChangeSuccess(null);
      }, 5000);
    } catch (err: any) {
      console.error('Email change error:', err);
      // Map Firebase error codes to user-friendly messages
      const code = err?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setEmailChangeError('Incorrect password. Please try again.');
      } else if (code === 'auth/email-already-in-use') {
        setEmailChangeError('This email is already associated with another account.');
      } else if (code === 'auth/invalid-email') {
        setEmailChangeError('The email address is not valid.');
      } else if (code === 'auth/requires-recent-login') {
        setEmailChangeError('Please log out and log back in, then try again.');
      } else if (code === 'auth/too-many-requests') {
        setEmailChangeError('Too many attempts. Please wait a moment and try again.');
      } else {
        setEmailChangeError(err?.message || 'Failed to update email. Please try again.');
      }
    } finally {
      setIsChangingEmail(false);
    }
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-[300px]" data-id="element-3428">
        <Loader2Icon className="h-8 w-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3429" />
        <span className="ml-2 text-gray-600 dark:text-[#B0C1BD]" data-id="element-3430">
          Loading profile...
        </span>
      </div>;
  }
  return <div data-id="element-3431">
      {/* Success message */}
      {successMessage && <div className="mb-6 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-md text-green-800 dark:text-green-400 flex items-start" data-id="element-3432">
          <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-3433" />
          <span data-id="element-3434">{successMessage}</span>
        </div>}

      {/* Error message */}
      {error && <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md text-red-800 dark:text-red-400 flex items-start" data-id="element-3435">
          <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-3436" />
          <span data-id="element-3437">{error}</span>
        </div>}

      <form onSubmit={handleSubmit} data-id="element-3438">
        <div className="mb-8" data-id="element-3439">
          {/* Banner Section */}
          <div className="relative mb-6" data-id="element-3440">
            <div className="w-full h-36 rounded-lg overflow-hidden cursor-pointer bg-gray-100 dark:bg-[#1A3330] flex items-center justify-center" onClick={handleBannerImageClick} data-id="element-3441">
              {bannerPreview ? <img src={bannerPreview} alt="Banner Preview" className="w-full h-full object-cover" data-id="element-3442" /> : formData.profileBanner ? <img src={formData.profileBanner} alt="" className="w-full h-full object-cover" data-id="element-3443" /> : <div className="w-full h-full bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#1A3330] dark:to-[#2DB489]" data-id="element-3444" />}
              {isBannerUploading && <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white" data-id="element-3445">
                  <span className="text-xs mb-1" data-id="element-3446">{bannerUploadProgress}%</span>
                  <div className="w-3/4 h-1.5 bg-gray-300 rounded-full overflow-hidden" data-id="element-3447">
                    <div className="h-full bg-white" style={{
                  width: `${bannerUploadProgress}%`
                }} data-id="element-3448"></div>
                  </div>
                </div>}
            </div>
            <input type="file" ref={bannerFileInputRef} onChange={handleBannerFileChange} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" data-id="element-3449" />
            <div className="absolute top-2 right-2" ref={bannerMenuRef} data-id="element-3450">
              <button type="button" onClick={() => setShowBannerMenu(prev => !prev)} className="text-white/80 hover:text-white drop-shadow-md transition-colors p-1" disabled={isBannerUploading} data-id="element-3451">
                <MoreVerticalIcon className="w-5 h-5" data-id="element-3452" />
              </button>
              {showBannerMenu && <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-[#162825] rounded-lg shadow-lg border border-gray-200 dark:border-[#1A3330] overflow-hidden z-10" data-id="element-3453">
                  <button type="button" onClick={() => {
                setShowBannerMenu(false);
                handleBannerImageClick();
              }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-3454">
                    <ImageIcon className="w-4 h-4" data-id="element-3455" />
                    Change Banner
                  </button>
                  <button type="button" onClick={handleRemoveBanner} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors" data-id="element-3456">
                    <Trash2Icon className="w-4 h-4" data-id="element-3457" />
                    Remove Banner
                  </button>
                </div>}
            </div>
            {/* Profile Image (positioned on the banner) */}
            <div className="absolute bottom-4 left-6" data-id="element-3458">
              <div className="relative" data-id="element-3459">
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white dark:border-[#162825] shadow-sm cursor-pointer bg-white dark:bg-[#162825]" onClick={handleProfileImageClick} data-id="element-3460">
                  {imagePreview ? <img src={imagePreview} alt="Profile Preview" className="w-full h-full object-cover" data-id="element-3461" /> : formData.profileImage ? <img src={formData.profileImage} alt="" className="w-full h-full object-cover" data-id="element-3462" /> : <div className="w-full h-full bg-[#2DB489] dark:bg-[#4ED1A0] flex items-center justify-center" data-id="element-3463">
                      <span className="text-white text-2xl font-semibold" data-id="element-3464">
                        {(formData.firstName?.[0] || user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                      </span>
                    </div>}
                  {isUploading && <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white" data-id="element-3465">
                      <span className="text-xs mb-1" data-id="element-3466">{uploadProgress}%</span>
                      <div className="w-3/4 h-1.5 bg-gray-300 rounded-full overflow-hidden" data-id="element-3467">
                        <div className="h-full bg-white" style={{
                      width: `${uploadProgress}%`
                    }} data-id="element-3468"></div>
                      </div>
                    </div>}
                </div>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" data-id="element-3469" />
                <button type="button" onClick={handleProfileImageClick} className="absolute -bottom-1 -right-1 bg-[#2DB489] dark:bg-[#4ED1A0] text-white p-1.5 rounded-full shadow-md hover:bg-[#28A07A] dark:hover:bg-[#3BC090] transition-colors border-2 border-white dark:border-[#162825]" disabled={isUploading} data-id="element-3470">
                  <CameraIcon className="w-4 h-4" data-id="element-3471" />
                </button>
              </div>
            </div>
          </div>
          <div className="mt-16 flex flex-col sm:flex-row gap-6 items-start sm:items-center mb-8" data-id="element-3472">
            <div className="flex-grow" data-id="element-3473">
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3474">
                {userData.firstName} {userData.lastName}
              </h3>
              <div className="flex items-center mt-1" data-id="element-3475">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${userData.isVerified ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'}`} data-id="element-3476">
                  {userData.isVerified ? <>
                      <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3477" />
                      Verified
                    </> : 'Not Verified'}
                </span>
              </div>
            </div>
            <button onClick={isEditing ? handleCancelEdit : () => setIsEditing(true)} className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" disabled={isSaving} data-id="element-3478">
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3479">
            {/* First Name */}
            <div data-id="element-3480">
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3481">
                First Name
              </label>
              {isEditing ? <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3482" /> : <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3483">
                  {userData.firstName || '—'}
                </p>}
            </div>
            {/* Last Name */}
            <div data-id="element-3484">
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3485">
                Last Name
              </label>
              {isEditing ? <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3486" /> : <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3487">
                  {userData.lastName || '—'}
                </p>}
            </div>
            {/* Public Username */}
            <div data-id="element-3488">
              <label htmlFor="publicUsername" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3489">
                Public Username <span className="text-red-500" data-id="element-3490">*</span>
              </label>
              {isEditing ? <div data-id="element-3491">
                  <input type="text" id="publicUsername" name="publicUsername" value={formData.publicUsername} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3492" />
                  <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3493">
                    This will be visible on your store and public profile. You
                    can't use spaces.
                  </p>
                  <p className="mt-1 text-sm text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-3494">
                    Your public page: your-domain.com/seller/
                    {formData.publicUsername}
                  </p>
                </div> : <div data-id="element-3495">
                  <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3496">
                    {userData.publicUsername || '—'}
                  </p>
                  {userData.publicUsername && <p className="mt-1 text-sm text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-3497">
                      Your public page: your-domain.com/seller/
                      {userData.publicUsername}
                    </p>}
                </div>}
            </div>
            {/* Email Address */}
            <div data-id="element-3498">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3499">
                Email Address
              </label>
              <div className="flex items-center gap-3" data-id="element-3500">
                <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3501">
                  {userData.email || '—'}
                </p>
                {!showEmailChange && <button type="button" onClick={() => {
                setShowEmailChange(true);
                setEmailChangeError(null);
                setEmailChangeSuccess(null);
                setNewEmail('');
                setEmailChangePassword('');
                setShowEmailPassword(false);
              }} className="text-xs font-medium text-[#2DB489] dark:text-[#4ED1A0] hover:underline flex items-center" data-id="element-3502">
                    <MailIcon className="h-3 w-3 mr-1" data-id="element-3503" />
                    Change Email
                  </button>}
              </div>
              {/* Email Change Form */}
              {showEmailChange && <div className="mt-3 p-4 bg-gray-50 dark:bg-[#1A3330] rounded-lg border border-gray-200 dark:border-[#1A3330]" data-id="element-3504">
                  <div className="flex items-center justify-between mb-3" data-id="element-3505">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3506">
                      Change Email Address
                    </h4>
                    <button type="button" onClick={() => {
                  setShowEmailChange(false);
                  setEmailChangeError(null);
                  setEmailChangeSuccess(null);
                }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-3507">
                      <XIcon className="h-4 w-4" data-id="element-3508" />
                    </button>
                  </div>
                  {emailChangeSuccess && <div className="mb-3 p-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-md text-green-800 dark:text-green-400 text-sm flex items-start" data-id="element-3509">
                      <CheckCircleIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" data-id="element-3510" />
                      <span data-id="element-3511">{emailChangeSuccess}</span>
                    </div>}
                  {emailChangeError && <div className="mb-3 p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md text-red-800 dark:text-red-400 text-sm flex items-start" data-id="element-3512">
                      <AlertCircleIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" data-id="element-3513" />
                      <span data-id="element-3514">{emailChangeError}</span>
                    </div>}
                  {!emailChangeSuccess && <form onSubmit={handleEmailChange} className="space-y-3" data-id="element-3515">
                      <div data-id="element-3516">
                        <label htmlFor="newEmail" className="block text-xs font-medium text-gray-600 dark:text-[#B0C1BD] mb-1" data-id="element-3517">
                          New Email Address
                        </label>
                        <input type="email" id="newEmail" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Enter new email address" className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-[#243F3B] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" disabled={isChangingEmail} required data-id="element-3518" />
                      </div>
                      <div data-id="element-3519">
                        <label htmlFor="emailChangePassword" className="block text-xs font-medium text-gray-600 dark:text-[#B0C1BD] mb-1" data-id="element-3520">
                          Current Password
                          <span className="text-gray-400 dark:text-[#7A8F8A] ml-1" data-id="element-3521">
                            (required for security)
                          </span>
                        </label>
                        <div className="relative" data-id="element-3522">
                          <input type={showEmailPassword ? 'text' : 'password'} id="emailChangePassword" value={emailChangePassword} onChange={e => setEmailChangePassword(e.target.value)} placeholder="Enter your current password" className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 dark:border-[#243F3B] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" disabled={isChangingEmail} required data-id="element-3523" />
                          <button type="button" onClick={() => setShowEmailPassword(!showEmailPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-3524">
                            {showEmailPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-3525" /> : <EyeIcon className="h-4 w-4" data-id="element-3526" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-1" data-id="element-3527">
                        <button type="submit" disabled={isChangingEmail} className="px-4 py-2 bg-[#2DB489] dark:bg-[#4ED1A0] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center" data-id="element-3528">
                          {isChangingEmail ? <>
                              <Loader2Icon className="h-3.5 w-3.5 mr-1.5 animate-spin" data-id="element-3529" />
                              Updating...
                            </> : 'Update Email'}
                        </button>
                        <button type="button" onClick={() => {
                    setShowEmailChange(false);
                    setEmailChangeError(null);
                    setEmailChangeSuccess(null);
                  }} disabled={isChangingEmail} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-[#B0C1BD] hover:text-gray-800 dark:hover:text-[#F8FAF9] transition-colors" data-id="element-3530">
                          Cancel
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-[#7A8F8A]" data-id="element-3531">
                        A verification email will be sent to your new address.
                        Your email won't change until you verify it.
                      </p>
                    </form>}
                </div>}
            </div>
            {/* Phone Number */}
            <div data-id="element-3532">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3533">
                Phone Number{' '}
                <span className="text-gray-400 dark:text-[#7A8F8A] text-xs" data-id="element-3534">
                  (Optional)
                </span>
              </label>
              {isEditing ? <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3535" /> : <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3536">
                  {userData.phone || '—'}
                </p>}
            </div>
            {/* Languages Spoken */}
            <div className="md:col-span-2" data-id="element-3537">
              <label htmlFor="languagesSpoken" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3538">
                Languages Spoken
              </label>
              {isEditing ? <div data-id="element-3539">
                  {/* Selected languages chips */}
                  <div className="flex flex-wrap gap-2 mb-3" data-id="element-3540">
                    {formData.languagesSpoken.map(language => <div key={language} className="flex items-center bg-[#2DB489]/10 text-[#2DB489] dark:bg-[#4ED1A0]/20 dark:text-[#4ED1A0] px-3 py-1 rounded-full text-sm" data-id="element-3541">
                        {language}
                        <button type="button" onClick={() => handleRemoveLanguage(language)} className="ml-1.5 text-[#2DB489] dark:text-[#4ED1A0] hover:text-[#28A07A] dark:hover:text-[#3BC090]" data-id="element-3542">
                          <XIcon className="w-3.5 h-3.5" data-id="element-3543" />
                        </button>
                      </div>)}
                  </div>
                  {/* Language selector */}
                  <div className="bg-white dark:bg-[#162825] border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm" data-id="element-3544">
                    <div className="max-h-48 overflow-y-auto p-2" data-id="element-3545">
                      {availableLanguages.map(language => <div key={language} className="flex items-center py-1.5 px-2 hover:bg-gray-50 dark:hover:bg-[#1A3330] rounded cursor-pointer" onClick={() => handleLanguageToggle(language)} data-id="element-3546">
                          <input type="checkbox" id={`language-${language}`} checked={formData.languagesSpoken.includes(language)} onChange={() => {}} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" data-id="element-3547" />
                          <label htmlFor={`language-${language}`} className="ml-2 block text-sm text-gray-700 dark:text-[#B0C1BD] cursor-pointer" data-id="element-3548">
                            {language}
                          </label>
                        </div>)}
                      {/* Add custom language option */}
                      {!showCustomLanguageInput ? <div className="flex items-center py-1.5 px-2 text-[#2DB489] dark:text-[#4ED1A0] hover:bg-gray-50 dark:hover:bg-[#1A3330] rounded cursor-pointer" onClick={() => setShowCustomLanguageInput(true)} data-id="element-3549">
                          <PlusIcon className="h-4 w-4 mr-2" data-id="element-3550" />
                          <span className="text-sm font-medium" data-id="element-3551">Add Other</span>
                        </div> : <div className="flex items-center p-2" data-id="element-3552">
                          <input type="text" value={customLanguage} onChange={e => setCustomLanguage(e.target.value)} placeholder="Enter language" className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-[#1A3330] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#F8FAF9]" data-id="element-3553" />
                          <button type="button" onClick={handleAddCustomLanguage} className="ml-2 px-2 py-1 bg-[#2DB489] dark:bg-[#4ED1A0] text-white text-sm rounded-md hover:bg-[#28A07A] dark:hover:bg-[#3BC090]" data-id="element-3554">
                            Add
                          </button>
                        </div>}
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3555">
                    This helps buyers know what languages you can communicate
                    in.
                  </p>
                </div> : <div data-id="element-3556">
                  {userData.languagesSpoken.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-3557">
                      {userData.languagesSpoken.map(language => <span key={language} className="bg-[#2DB489]/10 text-[#2DB489] dark:bg-[#4ED1A0]/20 dark:text-[#4ED1A0] px-3 py-1 rounded-full text-sm" data-id="element-3558">
                          {language}
                        </span>)}
                    </div> : <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3559">
                      No languages specified
                    </p>}
                </div>}
            </div>
          </div>
        </div>
        {isEditing && <div className="flex justify-end" data-id="element-3560">
            <button type="button" onClick={handleCancelEdit} className="px-5 py-2.5 mr-3 border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-all duration-200" disabled={isSaving} data-id="element-3561">
              Cancel
            </button>
            <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" disabled={isSaving} data-id="element-3562">
              {isSaving ? <>
                  <Loader2Icon className="h-4 w-4 mr-2 animate-spin" data-id="element-3563" />
                  Saving...
                </> : 'Save Changes'}
            </button>
          </div>}
      </form>
    </div>;
};